import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { CreateSupplierPurchasableExtraInput, Currency } from "@/gql/graphql";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { supplierPurchasableExtraValidation } from "@/app/Suppliers/Validation/supplierPurchasableExtraValidation";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { NumberInput } from "@/lib/Components/Form/Inputs/NumberInput";
import { createPurchasableExtrasQuery } from "@/app/Suppliers/GraphQL/supplierPurchasableExtrasQuery";
import { useSupplier } from "@/app/Suppliers/Hooks/useSupplierRecord";
import { DialogComponent } from "@/lib/Components/Dialog/Hooks/useDialog";
import { SupplierPurchasableExtraListItem } from "@/app/Suppliers/GraphQL/supplierPurchasableExtraList";
import { Field, Label } from "@/components/catalyst/fieldset";
import { Switch } from "@headlessui/react";
import { useField } from "formik";
import { clsx } from "clsx";

type SupplierPurchasableExtraCreateDialogProps = {
  supplierId: string;
};

export const SupplierPurchasableExtraCreateDialog: DialogComponent<
  SupplierPurchasableExtraCreateDialogProps,
  SupplierPurchasableExtraListItem
> = ({ supplierId }) => {
  const supplier = useSupplier(supplierId);
  const { close, resolve } = useDialogState<SupplierPurchasableExtraListItem>();
  const { mutateAsync } = useGqlMutation(createPurchasableExtrasQuery);

  return (
    <BaseDialog title="Add purchasable extra">
      <GenericForm<CreateSupplierPurchasableExtraInput>
        validationSchema={toFormikValidationSchema(
          supplierPurchasableExtraValidation,
        )}
        initialValues={{
          name: "",
          description: "",
          supplier: {
            connect: supplier.id,
          },
          units_purchasable: 1,
          unit_price: null!,
        }}
        onSubmit={async (values) => {
          const res = await mutateAsync({
            input: values,
          });

          resolve?.(res.createSupplierPurchasableExtra);

          await close();
        }}
      >
        <SupplierPurchasableExtraForm currency={supplier.currency} />
        <DialogSubmitButton text={"Add purchasable extra"} />
      </GenericForm>
    </BaseDialog>
  );
};

export function SupplierPurchasableExtraForm({
  currency,
}: {
  currency: Currency;
}) {
  return (
    <>
      <TextInput
        name="name"
        label="Name"
        autoFocus
        inputProps={{ placeholder: "e.g. Child seat" }}
      />
      <NumberInput
        name="units_purchasable"
        label="Units purchasable"
        inputProps={{
          min: 0,
        }}
      />

      <UnitPriceInput currency={currency} />
      <TextAreaInput
        name={"description"}
        label={"Description"}
        className="col-span-full"
      />
    </>
  );
}

function UnitPriceInput({ currency }: { currency: Currency }) {
  const [{ value }, , { setValue }] = useField<number | null>("unit_price");
  const isFree = value === 0;

  return (
    <>
      <Field className="col-span-3 min-h-[60px] max-lg:col-span-full">
        <Label>Free of charge</Label>
        <div>
          <Switch
            checked={isFree}
            onChange={(checked) => setValue(checked ? 0 : null)}
            className={clsx(
              "relative mt-1 inline-flex h-6 w-11 items-center rounded-full",
              isFree ? "bg-blue-600" : "bg-gray-200",
            )}
          >
            <span
              className={clsx(
                "inline-block size-4 transform rounded-full bg-white transition",
                isFree ? "translate-x-6" : "translate-x-1",
              )}
            />
          </Switch>
        </div>
      </Field>

      {isFree ? null : (
        <CurrencyInput
          currency={currency}
          name="unit_price"
          label="Unit price"
        />
      )}
    </>
  );
}
